import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Fade,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import RestoreIcon from "@mui/icons-material/Restore";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import useDebouncedValue from "../Assessments/useDebouncedValue";
import {
  apiErrorMessage,
  deleteCampaignLink,
  getCampaignLinkFilters,
  listCampaignLinks,
  restoreCampaignLink,
} from "../../api/campaignLinks";
import CampaignLinkDetailsDialog from "./CampaignLinkDetailsDialog";
import { copyToClipboard, formatDateTime } from "./campaignLinkUrl";

const headerCell = {
  padding: "10px 5px",
  backgroundColor: "#17479e",
  color: "white",
  fontWeight: 600,
  textAlign: "center",
};

const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "DELETED", label: "Deleted" },
  { value: "ALL", label: "All" },
];

const COLUMN_COUNT = 9;

function AuditCell({ name, date }) {
  return (
    <TableCell align="center">
      <Typography variant="body2">{name || "—"}</Typography>
      <Typography variant="caption" color="text.secondary">
        {formatDateTime(date)}
      </Typography>
    </TableCell>
  );
}

function CampaignLinks() {
  const navigate = useNavigate();
  const location = useLocation();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [source, setSource] = useState("");
  const [medium, setMedium] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  const [filterOptions, setFilterOptions] = useState({ sources: [], mediums: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [viewId, setViewId] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);

  const debouncedSearch = useDebouncedValue(search, 300);
  const hasFilters = Boolean(search || source || medium || status !== "ACTIVE");

  // Message handed over by the form after a save.
  useEffect(() => {
    if (location.state?.message) {
      toast.success(location.state.message, { position: "top-right", autoClose: 4000 });
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  useEffect(() => {
    getCampaignLinkFilters()
      .then((data) => setFilterOptions({ sources: data?.sources || [], mediums: data?.mediums || [] }))
      .catch(() => {}); // filter dropdowns just stay empty
  }, [reloadKey]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, source, medium, status]);

  useEffect(() => {
    let ignore = false; // a newer filter/page request replaced this one
    setIsLoading(true);
    setError(null);
    listCampaignLinks({
      search: debouncedSearch.trim() || undefined,
      source: source || undefined,
      medium: medium || undefined,
      status,
      page,
      size: rowsPerPage,
    })
      .then((data) => {
        if (ignore) return;
        setRows(Array.isArray(data?.content) ? data.content : []);
        setTotal(data?.totalElements || 0);
      })
      .catch((err) => {
        if (ignore) return;
        console.error(err);
        setRows([]);
        setTotal(0);
        setError(apiErrorMessage(err, "Failed to load campaigns. Please try again."));
      })
      .finally(() => {
        if (!ignore) setIsLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [debouncedSearch, source, medium, status, page, rowsPerPage, reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);

  const clearFilters = () => {
    setSearch("");
    setSource("");
    setMedium("");
    setStatus("ACTIVE");
  };

  const handleCopy = useCallback(async (url) => {
    try {
      await copyToClipboard(url);
      toast.success("Link copied to clipboard!", { position: "top-right", autoClose: 2000 });
    } catch {
      toast.error("Could not copy the link. Open the campaign and copy it manually.");
    }
  }, []);

  const openEdit = (row) => navigate(`/campaign-links/${row.id}/edit`);
  const openDuplicate = (row) => navigate(`/campaign-links/new?copyFrom=${row.id}`);

  const handleDelete = async () => {
    try {
      await deleteCampaignLink(deleteRow.id);
      toast.success("Campaign deleted. You can restore it from the Deleted filter.", {
        position: "top-right",
        autoClose: 3000,
      });
      // Step back a page when the last row of a page goes away.
      if (rows.length === 1 && page > 0) setPage((p) => p - 1);
      else reload();
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error(apiErrorMessage(err, "Failed to delete the campaign. Please try again."));
    } finally {
      setDeleteRow(null);
    }
  };

  const handleRestore = async (row) => {
    try {
      await restoreCampaignLink(row.id);
      toast.success("Campaign restored successfully!", { position: "top-right", autoClose: 3000 });
      reload();
    } catch (err) {
      console.error("Restore failed:", err);
      toast.error(apiErrorMessage(err, "Failed to restore the campaign. Please try again."), { autoClose: 5000 });
    }
  };

  const filterSelect = (label, value, onChange, options) => (
    <FormControl size="small" sx={{ minWidth: 160, flex: { xs: "1 1 140px", md: "0 0 auto" } }}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} onChange={(e) => onChange(e.target.value)}>
        <MenuItem value="">All</MenuItem>
        {options.map((o) => (
          <MenuItem key={o} value={o}>
            {o}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <ToastContainer />
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, mt: 1 }}>
        <Typography sx={{ color: "text.primary", fontWeight: 600 }}>Campaign Links</Typography>
      </Breadcrumbs>

      <Card sx={{ boxShadow: 1, mb: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", md: "center" },
              gap: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Tracking links to the SIA Android app for marketing campaigns. {total} campaign
              {total === 1 ? "" : "s"}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate("/campaign-links/new")}
              {...BUTTON_STYLES.primary}
              sx={{ minWidth: { xs: "120px", md: "auto" }, fontSize: { xs: "10px", md: "12px" } }}
            >
              New Campaign
            </Button>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 2, alignItems: "center" }}>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Search by campaign name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              inputProps={{ "aria-label": "Search campaigns" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: { xs: "1 1 100%", md: "0 1 360px" } }}
            />
            {filterSelect("Source", source, setSource, filterOptions.sources)}
            {filterSelect("Medium", medium, setMedium, filterOptions.mediums)}
            <FormControl size="small" sx={{ minWidth: 130, flex: { xs: "1 1 120px", md: "0 0 auto" } }}>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {hasFilters && (
              <Button size="small" onClick={clearFilters} sx={{ textTransform: "none" }}>
                Clear filters
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && (
        <Fade in={!isLoading}>
          <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 2 }}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small" aria-label="campaign links table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headerCell, minWidth: 160 }}>Campaign Name</TableCell>
                    <TableCell sx={headerCell}>Source</TableCell>
                    <TableCell sx={headerCell}>Medium</TableCell>
                    <TableCell sx={headerCell}>Destination</TableCell>
                    <TableCell sx={{ ...headerCell, minWidth: 220 }}>Generated Link</TableCell>
                    <TableCell sx={{ ...headerCell, minWidth: 130 }}>Created</TableCell>
                    <TableCell sx={{ ...headerCell, minWidth: 130 }}>Last Updated</TableCell>
                    <TableCell sx={headerCell}>Status</TableCell>
                    <TableCell sx={{ ...headerCell, minWidth: 150 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length > 0 ? (
                    rows.map((row) => {
                      const isDeleted = row.status === "DELETED";
                      return (
                        <TableRow
                          hover
                          key={row.id}
                          sx={{
                            "&:nth-of-type(odd)": { backgroundColor: "rgba(0, 0, 0, 0.02)" },
                            opacity: isDeleted ? 0.7 : 1,
                          }}
                        >
                          <TableCell align="center" sx={{ fontWeight: 600 }}>
                            {row.utmCampaign}
                          </TableCell>
                          <TableCell align="center">{row.utmSource}</TableCell>
                          <TableCell align="center">{row.utmMedium}</TableCell>
                          <TableCell align="center">
                            {row.destination === "ANDROID_APP" ? "Android app" : row.destinationLabel || row.destination}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Tooltip title={row.generatedUrl} placement="top">
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: "monospace",
                                    fontSize: "0.75rem",
                                    maxWidth: 220,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.generatedUrl}
                                </Typography>
                              </Tooltip>
                              <Tooltip title="Copy Link" arrow placement="top">
                                <IconButton
                                  color="primary"
                                  size="small"
                                  aria-label={`Copy link for ${row.utmCampaign}`}
                                  onClick={() => handleCopy(row.generatedUrl)}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <AuditCell name={row.createdByName} date={row.createdAt} />
                          <AuditCell name={row.updatedByName} date={row.updatedAt} />
                          <TableCell align="center">
                            <Chip
                              label={isDeleted ? "Deleted" : "Active"}
                              color={isDeleted ? "error" : "success"}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <Tooltip title="View Details" arrow placement="top">
                                <IconButton
                                  color="primary"
                                  size="small"
                                  aria-label={`View ${row.utmCampaign}`}
                                  onClick={() => setViewId(row.id)}
                                  sx={{ "&:hover": { backgroundColor: "primary.light", color: "white" } }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {!isDeleted && (
                                <Tooltip title="Edit Campaign" arrow placement="top">
                                  <IconButton
                                    color="primary"
                                    size="small"
                                    aria-label={`Edit ${row.utmCampaign}`}
                                    onClick={() => openEdit(row)}
                                    sx={{ "&:hover": { backgroundColor: "primary.light", color: "white" } }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Duplicate as New Campaign" arrow placement="top">
                                <IconButton
                                  color="primary"
                                  size="small"
                                  aria-label={`Duplicate ${row.utmCampaign}`}
                                  onClick={() => openDuplicate(row)}
                                  sx={{ "&:hover": { backgroundColor: "primary.light", color: "white" } }}
                                >
                                  <FileCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {isDeleted ? (
                                <Tooltip title="Restore Campaign" arrow placement="top">
                                  <IconButton
                                    color="success"
                                    size="small"
                                    aria-label={`Restore ${row.utmCampaign}`}
                                    onClick={() => handleRestore(row)}
                                    sx={{ "&:hover": { backgroundColor: "success.light", color: "white" } }}
                                  >
                                    <RestoreIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Delete Campaign" arrow placement="top">
                                  <IconButton
                                    color="error"
                                    size="small"
                                    aria-label={`Delete ${row.utmCampaign}`}
                                    onClick={() => setDeleteRow(row)}
                                    sx={{ "&:hover": { backgroundColor: "error.light", color: "white" } }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={COLUMN_COUNT} sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="h6" color="text.secondary">
                          {hasFilters ? "No campaigns match your filters" : "No campaigns yet"}
                        </Typography>
                        {hasFilters ? (
                          <Button sx={{ mt: 1, textTransform: "none" }} onClick={clearFilters}>
                            Clear filters
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Click "New Campaign" to create your first tracking link.
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                "& .MuiTablePagination-toolbar": { paddingLeft: 2, paddingRight: 2 },
              }}
            />
          </Paper>
        </Fade>
      )}

      <CampaignLinkDetailsDialog
        campaignId={viewId}
        onClose={() => setViewId(null)}
        onCopy={handleCopy}
        onEdit={openEdit}
        onDuplicate={openDuplicate}
      />

      <DeleteConfirmationDialog
        open={deleteRow != null}
        onClose={() => setDeleteRow(null)}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message={`Are you sure you want to delete the campaign "${deleteRow?.utmCampaign || ""}"? You can restore it later from the Deleted filter.`}
        additionalWarning="Deleting only removes the campaign from this list. Links already shared with students keep working; a Play Store link cannot be switched off."
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Box>
  );
}

export default CampaignLinks;
